import { useTranslation } from 'react-i18next';
import { ScrollArea } from '@components/ui/ScrollArea/ScrollArea';
import { BuildingSkeleton } from '@components/BuildingSkeleton/BuildingSkeleton';
import classes from './monitorSkeleton.module.css';

/**
 * Monitor page skeleton. Mirrors the real layout closely so the swap from
 * loading → loaded doesn't reflow the page:
 *   - Left rail: a card stack — a live-summary card, three filter cards
 *     (professions, managers, equipment), and a safety-alerts card.
 *   - Main view: a building silhouette with stacked floor slabs (each
 *     carrying a row of "chips" so the shimmer hints at trade/manager
 *     pills), then the Special Areas card table.
 *
 * Row counts and widths are deliberately uneven — uniform skeletons read as
 * generic placeholders, jagged ones read as "real content is loading".
 */
export function MonitorSkeleton() {
  const { t } = useTranslation();
  return (
    <div className={classes.skeleton} aria-busy="true" aria-label={t('monitor.skeleton.loading')}>
      <LeftRailSkeleton />
      <MainViewSkeleton />
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Left rail
// ──────────────────────────────────────────────────────────────────────────────

function LeftRailSkeleton() {
  return (
    <aside className={classes.leftRail}>
      <LiveSummaryCardSkeleton />
      <FilterCardSkeleton rows={5} variant="profession" />
      <FilterCardSkeleton rows={3} variant="manager" />
      <FilterCardSkeleton rows={4} variant="equipment" />
      <SafetyAlertsCardSkeleton />
    </aside>
  );
}

/**
 * Card header — leading glyph + title bar, with an optional trailing block
 * (the filter-section count, or the Safety card's "view all").
 */
function CardHeader({ extra = false }: { extra?: boolean }) {
  return (
    <div className={classes.cardHeader}>
      <span className={`${classes.shimmer} ${classes.headerIcon}`} />
      <span className={`${classes.shimmer} ${classes.headerTitle}`} />
      {extra && <span className={`${classes.shimmer} ${classes.headerExtra}`} />}
    </div>
  );
}

/** Live Summary card — header + a 2x2 grid of stat tiles. */
function LiveSummaryCardSkeleton() {
  return (
    <div className={classes.card}>
      <CardHeader />
      <div className={classes.summaryGrid}>
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className={classes.summaryTile}>
            <span className={`${classes.shimmer} ${classes.tileValue}`} />
            <span className={`${classes.shimmer} ${classes.tileLabel}`} />
          </div>
        ))}
      </div>
    </div>
  );
}

const PROF_WIDTHS = ['72%', '58%', '84%', '46%', '64%', '78%'];
const MANAGER_WIDTHS = ['62%', '74%', '50%', '68%'];
const EQUIPMENT_WIDTHS = ['66%', '54%', '78%', '60%', '70%'];

/** Collapsible filter card — Professions / Managers / Equipment. */
function FilterCardSkeleton({
  rows,
  variant,
}: {
  rows: number;
  variant: 'profession' | 'manager' | 'equipment';
}) {
  return (
    <div className={classes.card}>
      <CardHeader extra />
      <div className={classes.cardBody}>
        {Array.from({ length: rows }).map((_, i) => (
          <FilterRow key={i} variant={variant} index={i} />
        ))}
      </div>
    </div>
  );
}

const SAFETY_ROW_WIDTHS = ['82%', '66%', '90%'];

/** Safety Alerts card — header + three two-line alert rows. */
function SafetyAlertsCardSkeleton() {
  return (
    <div className={classes.card}>
      <CardHeader extra />
      <div className={classes.cardBody}>
        {SAFETY_ROW_WIDTHS.map((w, i) => (
          <div key={i} className={classes.safetyRow}>
            <span
              className={`${classes.shimmer} ${classes.safetyLine1}`}
              style={{ width: w }}
            />
            <span
              className={`${classes.shimmer} ${classes.safetyLine2}`}
              style={{ width: '48%' }}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

function FilterRow({
  variant,
  index,
}: {
  variant: 'profession' | 'manager' | 'equipment';
  index: number;
}) {
  if (variant === 'profession') {
    const w = PROF_WIDTHS[index % PROF_WIDTHS.length];
    return (
      <div className={classes.row}>
        <span className={`${classes.shimmer} ${classes.checkbox}`} />
        <span
          className={`${classes.shimmer} ${classes.chipPill}`}
          style={{ width: w }}
        />
        <span className={`${classes.shimmer} ${classes.rowCount}`} />
      </div>
    );
  }
  if (variant === 'manager') {
    const w = MANAGER_WIDTHS[index % MANAGER_WIDTHS.length];
    return (
      <div className={classes.row}>
        <span className={`${classes.shimmer} ${classes.checkbox}`} />
        <span className={`${classes.shimmer} ${classes.avatar}`} />
        <span
          className={`${classes.shimmer} ${classes.rowText}`}
          style={{ width: w }}
        />
        <span
          className={`${classes.shimmer} ${classes.rowMeta}`}
          style={{ width: '28%' }}
        />
      </div>
    );
  }
  const w = EQUIPMENT_WIDTHS[index % EQUIPMENT_WIDTHS.length];
  return (
    <div className={classes.row}>
      <span className={`${classes.shimmer} ${classes.checkbox}`} />
      <span className={`${classes.shimmer} ${classes.iconBox}`} />
      <span
        className={`${classes.shimmer} ${classes.rowText}`}
        style={{ width: w }}
      />
      <span className={`${classes.shimmer} ${classes.rowCount}`} />
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Main view — building silhouette + Special Areas table
// ──────────────────────────────────────────────────────────────────────────────

function MainViewSkeleton() {
  return (
    <section className={classes.mainView}>
      <ScrollArea className={classes.mainBody} viewportClassName={classes.mainBodyViewport}>
        <BuildingCardSkeleton />
        <SpecialAreasSkeleton />
      </ScrollArea>
    </section>
  );
}

/**
 * Building card — same chrome as the loaded `<section className="card">` in
 * `MainView.tsx`: white surface + g100 border + r-3xl + shadow-card, with a
 * bordered header row and the tower below. The title pill and the three
 * legend chips shimmer in place of the real "Building side-view — RTLS
 * live" + legend text, matching the LeftRail card-header treatment.
 */
function BuildingCardSkeleton() {
  return (
    <div className={classes.buildingCard}>
      <div className={classes.cardHeaderRow}>
        <span className={`${classes.shimmer} ${classes.headerTitle}`} />
        <div className={classes.legend}>
          <span className={`${classes.shimmer} ${classes.legendChip}`} />
          <span className={`${classes.shimmer} ${classes.legendChip}`} />
          <span className={`${classes.shimmer} ${classes.legendChip}`} />
        </div>
      </div>
      <div className={classes.buildingWrap}>
        <BuildingSkeleton />
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Special Areas — table-style rows below the tower
// ──────────────────────────────────────────────────────────────────────────────

const AREA_NAME_WIDTHS = ['62%', '74%', '54%', '68%'];
const AREA_CHIP_PATTERN: Array<Array<{ w: string; kind: 'prof' | 'manager' | 'equip' }>> = [
  [{ w: '92px', kind: 'prof' }, { w: '78px', kind: 'prof' }, { w: '64px', kind: 'manager' }],
  [{ w: '110px', kind: 'prof' }, { w: '46px', kind: 'equip' }],
  [{ w: '84px', kind: 'prof' }, { w: '70px', kind: 'manager' }, { w: '46px', kind: 'equip' }],
  [{ w: '96px', kind: 'prof' }],
];

function SpecialAreasSkeleton() {
  return (
    <div className={classes.specialAreas}>
      <span className={`${classes.shimmer} ${classes.specialAreasTitle}`} />

      <div className={classes.specialAreasHead}>
        <span className={`${classes.shimmer} ${classes.headCell}`} style={{ width: '40%' }} />
        <span className={`${classes.shimmer} ${classes.headCell}`} style={{ width: '24%' }} />
        <span className={`${classes.shimmer} ${classes.headCell}`} style={{ width: '32%', justifySelf: 'end' }} />
      </div>

      {[0, 1, 2, 3].map((i) => (
        <SpecialAreaRow key={i} index={i} />
      ))}
    </div>
  );
}

function SpecialAreaRow({ index }: { index: number }) {
  const chips = AREA_CHIP_PATTERN[index % AREA_CHIP_PATTERN.length] ?? [];
  const showAlert = index === 1;
  return (
    <div className={classes.areaRow}>
      <div className={classes.areaTitleCol}>
        <div className={classes.areaTitleHead}>
          <span className={`${classes.shimmer} ${classes.areaIcon}`} />
          <span
            className={`${classes.shimmer} ${classes.areaName}`}
            style={{ width: AREA_NAME_WIDTHS[index % AREA_NAME_WIDTHS.length] }}
          />
        </div>
        {index % 2 === 1 && (
          <span className={`${classes.shimmer} ${classes.areaMeta}`} style={{ width: '54%' }} />
        )}
      </div>

      <div className={classes.areaResourceRow}>
        {chips.map((c, i) => (
          <span
            key={i}
            className={`${classes.shimmer} ${classes.areaChip} ${classes[`chip_${c.kind}`]}`}
            style={{ width: c.w }}
          />
        ))}
      </div>

      <div className={classes.areaInfoCol}>
        {showAlert && <span className={`${classes.shimmer} ${classes.areaAlert}`} />}
      </div>
    </div>
  );
}

